import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { useAuthContext } from '~/shared/context/useAuthContext';

import { buildJsonLogicRule } from '../../../common/buildJsonLogic';
import { injectAutotags } from '../developmentAutoTagging';

import type { Condition } from '../../useAutoscaleRulesBuilder';

interface CreateAutoscalingRuleRequest {
  active: boolean;
  rule: Record<string, unknown>;
}

interface CreateAutoscalingRuleResponse {
  ruleId: string;
  active: boolean;
  rule: Record<string, unknown>;
}

export function useCreateAutoscalingRule(editRuleId?: string) {
  const api = useXFetch();
  const queryClient = useQueryClient();
  const { user, accounts } = useAuthContext();
  const isEditMode = Boolean(editRuleId);

  return useMutation({
    mutationFn: async ({
      conditions,
      active,
      accountId,
    }: {
      conditions: Condition[];
      active: boolean;
      accountId?: string;
    }): Promise<CreateAutoscalingRuleResponse> => {
      if (conditions.length === 0) {
        throw new Error('No conditions to process');
      }

      const conditionsWithAutotags = injectAutotags(conditions, user?.email, accounts);

      // Build JSON Logic from each condition
      const conditionJsonLogicRules = conditionsWithAutotags
        .map((condition) => buildJsonLogicRule(condition))
        .filter(
          (logic): logic is Record<string, unknown> | Record<string, unknown>[] => logic !== null
        )
        .flat();

      if (conditionJsonLogicRules.length === 0) {
        throw new Error('No valid conditions to process');
      }

      const jsonLogicRule =
        conditionJsonLogicRules.length === 1
          ? conditionJsonLogicRules[0]
          : { and: conditionJsonLogicRules };

      const requestBody: CreateAutoscalingRuleRequest = {
        active,
        rule: jsonLogicRule,
      };

      let url = '/api/v1/volumes/autoscaling/rules';

      if (isEditMode) {
        url = `/api/v1/volumes/autoscaling/rules/${editRuleId}`;
      } else if (accountId) {
        url = `/api/v1/accounts/${accountId}/autoscaling/rules`;
      }

      const response = await api<CreateAutoscalingRuleResponse>(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['autoscaleRules'] });

      if (isEditMode && editRuleId) {
        queryClient.invalidateQueries({ queryKey: ['autoscaleRuleDetails', editRuleId] });
      } else if (data.ruleId) {
        queryClient.invalidateQueries({ queryKey: ['autoscaleRuleDetails', data.ruleId] });
      }
    },
  });
}
